// src/components/ui/PracticeCalendarModal.tsx
import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";

import type { PracticeCalendarHomework } from "../ChatContainer";

interface PracticeCalendarModalProps {
  open: boolean;
  homework: PracticeCalendarHomework[];
  onClose: () => void;
}

type CalendarEventType =
  | "completed"
  | "attempted"
  | "missed"
  | "due"
  | "incomplete";

type CalendarDay = {
  date: Date;
  dateKey: string;
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  homework: PracticeCalendarHomework[];
};

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const EVENT_LABELS: Record<CalendarEventType, string> = {
  completed: "Completed",
  attempted: "Attempted",
  missed: "Missed",
  due: "Due",
  incomplete: "Incomplete",
};

const EVENT_DOT_CLASSES: Record<CalendarEventType, string> = {
  completed: "bg-emerald-700",
  attempted: "bg-emerald-300",
  due: "bg-red-600",
  missed: "bg-orange-300",
  incomplete: "bg-gray-400",
};

const EVENT_BADGE_CLASSES: Record<CalendarEventType, string> = {
  completed: "bg-emerald-700 text-white",
  attempted: "bg-emerald-100 text-emerald-800",
  missed: "bg-orange-100 text-orange-800",
  due: "bg-red-100 text-red-800",
  incomplete: "bg-gray-100 text-gray-700",
};

function dateToKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function normalizeDateKey(value: string | null | undefined): string | null {
  if (!value) return null;

  const dateOnlyMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);

  if (dateOnlyMatch) {
    return dateOnlyMatch[1];
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return dateToKey(parsedDate);
}

function dateFromKey(dateKey: string): Date {
  const [year, month, day] = dateKey.split("-").map(Number);

  return new Date(year, month - 1, day);
}

function formatMonth(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
  }).format(date);
}

function formatSelectedDate(dateKey: string): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(dateFromKey(dateKey));
}

function getEventType(item: PracticeCalendarHomework): CalendarEventType {
  if (item.eventType) {
    return item.eventType;
  }

  const status = item.completionStatus?.trim().toLowerCase();

  if (status === "completed") return "completed";
  if (status === "attempted") return "attempted";
  if (item.isPastDue) return "missed";
  if (item.dueDate) return "due";

  return "incomplete";
}

function getCalendarDate(item: PracticeCalendarHomework): string | null {
  return (
    normalizeDateKey(item.calendarDate) ??
    normalizeDateKey(item.completedAttemptedDate) ??
    normalizeDateKey(item.dueDate)
  );
}

function buildCalendarDays(
  displayedMonth: Date,
  homeworkByDate: Map<string, PracticeCalendarHomework[]>,
): CalendarDay[] {
  const year = displayedMonth.getFullYear();
  const month = displayedMonth.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const gridStart = new Date(year, month, 1 - firstDayOfMonth.getDay());

  const todayKey = dateToKey(new Date());

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);

    const dateKey = dateToKey(date);

    return {
      date,
      dateKey,
      dayNumber: date.getDate(),
      isCurrentMonth: date.getMonth() === month,
      isToday: dateKey === todayKey,
      homework: homeworkByDate.get(dateKey) ?? [],
    };
  });
}

export default function PracticeCalendarModal({
  open,
  homework,
  onClose,
}: PracticeCalendarModalProps) {
  const [displayedMonth, setDisplayedMonth] = useState(() => {
    const datedHomework = homework.find((item) => getCalendarDate(item));
    const initialDateKey = datedHomework
      ? getCalendarDate(datedHomework)
      : null;

    return initialDateKey ? dateFromKey(initialDateKey) : new Date();
  });

  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setSelectedDateKey(null);
    }
  }, [open]);

  const homeworkByDate = useMemo(() => {
    const grouped = new Map<string, PracticeCalendarHomework[]>();

    homework.forEach((item) => {
      const dateKey = getCalendarDate(item);

      if (!dateKey) return;

      const currentItems = grouped.get(dateKey) ?? [];
      currentItems.push(item);
      grouped.set(dateKey, currentItems);
    });

    return grouped;
  }, [homework]);

  const calendarDays = useMemo(
    () => buildCalendarDays(displayedMonth, homeworkByDate),
    [displayedMonth, homeworkByDate],
  );

  const selectedHomework = selectedDateKey
    ? (homeworkByDate.get(selectedDateKey) ?? [])
    : [];

  const unscheduledHomework = useMemo(
    () =>
      homework.filter((item) => {
        if (getCalendarDate(item)) return false;

        const status = item.completionStatus?.trim().toLowerCase();

        return status !== "completed" && status !== "attempted";
      }),
    [homework],
  );

  if (!open) return null;

  const goToPreviousMonth = () => {
    setDisplayedMonth(
      (current) => new Date(current.getFullYear(), current.getMonth() - 1, 1),
    );
    setSelectedDateKey(null);
  };

  const goToNextMonth = () => {
    setDisplayedMonth(
      (current) => new Date(current.getFullYear(), current.getMonth() + 1, 1),
    );
    setSelectedDateKey(null);
  };

  const goToCurrentMonth = () => {
    const today = new Date();

    setDisplayedMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDateKey(dateToKey(today));
  };

  return (
    <div
      className="fixed inset-x-0 top-0 z-50 flex h-[100dvh] items-center justify-center bg-black/40 p-3 sm:p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="practice-calendar-title"
        className="relative flex max-h-[calc(100dvh-1.5rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl sm:max-h-[92dvh]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-start border-b px-4 py-4 pr-16 sm:px-5 sm:pr-16">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <CalendarDays className="h-5 w-5" />
            </div>

            <div>
              <h2
                id="practice-calendar-title"
                className="text-lg font-semibold text-gray-900"
              >
                Practice Calendar
              </h2>

              <p className="text-sm text-gray-500">
                See your homework practice and due dates
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close Practice Calendar"
            className="absolute right-3 top-3 inline-flex h-11 w-11 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-300"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="overflow-y-auto px-3 py-4 sm:px-5">
          {/* Month navigation */}
          <div className="mb-4 flex items-center justify-between">
            <button
              type="button"
              onClick={goToPreviousMonth}
              aria-label="Previous month"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-300"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            <button
              type="button"
              onClick={goToCurrentMonth}
              className="rounded-lg px-3 py-1.5 text-base font-semibold text-gray-900 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-300"
              title="Return to current month"
            >
              {formatMonth(displayedMonth)}
            </button>

            <button
              type="button"
              onClick={goToNextMonth}
              aria-label="Next month"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-300"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          {/* Status legend */}
          <div className="mb-4 flex flex-wrap justify-center gap-x-3 gap-y-1.5 text-[11px] text-gray-600 sm:text-xs">
            {(
              ["completed", "attempted", "due", "missed"] as CalendarEventType[]
            ).map((eventType) => (
              <div key={eventType} className="flex items-center gap-1.5">
                <span
                  className={`h-2 w-2 rounded-full ${EVENT_DOT_CLASSES[eventType]}`}
                />
                <span>{EVENT_LABELS[eventType]}</span>
              </div>
            ))}
          </div>

          {/* Calendar */}
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <div className="grid grid-cols-7 bg-gray-50">
              {WEEKDAY_LABELS.map((weekday) => (
                <div
                  key={weekday}
                  className="py-2 text-center text-[11px] font-medium text-gray-500 sm:text-xs"
                >
                  {weekday}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7">
              {calendarDays.map((day) => {
                const isSelected = selectedDateKey === day.dateKey;
                const visibleEventTypes = Array.from(
                  new Set(day.homework.map(getEventType)),
                ).slice(0, 4);

                return (
                  <button
                    key={day.dateKey}
                    type="button"
                    onClick={() => {
                      setSelectedDateKey(day.dateKey);

                      if (!day.isCurrentMonth) {
                        setDisplayedMonth(
                          new Date(
                            day.date.getFullYear(),
                            day.date.getMonth(),
                            1,
                          ),
                        );
                      }
                    }}
                    aria-label={`${formatSelectedDate(day.dateKey)}${day.homework.length
                      ? `, ${day.homework.length} homework ${day.homework.length === 1
                        ? "activity"
                        : "activities"
                      }`
                      : ""
                      }`}
                    className={`relative min-h-[54px] border-r border-t border-gray-100 p-1 text-left transition-colors sm:min-h-[62px] sm:p-1.5 ${isSelected
                      ? "bg-emerald-50 ring-2 ring-inset ring-emerald-500"
                      : "hover:bg-gray-50"
                      }`}
                  >
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full text-xs sm:text-sm ${day.isToday
                        ? "bg-emerald-600 font-semibold text-white"
                        : day.isCurrentMonth
                          ? "text-gray-800"
                          : "text-gray-300"
                        }`}
                    >
                      {day.dayNumber}
                    </span>

                    {visibleEventTypes.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-0.5">
                        {visibleEventTypes.map((eventType) => (
                          <span
                            key={eventType}
                            className={`h-1.5 w-1.5 rounded-full sm:h-2 sm:w-2 ${EVENT_DOT_CLASSES[eventType]}`}
                          />
                        ))}

                        {day.homework.length > visibleEventTypes.length && (
                          <span className="text-[8px] leading-none text-gray-500 sm:text-[9px]">
                            +{day.homework.length - visibleEventTypes.length}
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected date details */}
          {selectedDateKey && (
            <section className="mt-5">
              <h3 className="text-sm font-semibold text-gray-900">
                {formatSelectedDate(selectedDateKey)}
              </h3>

              {selectedHomework.length === 0 ? (
                <div className="mt-2 rounded-xl bg-gray-50 px-4 py-4 text-center text-sm text-gray-500">
                  No homework activity on this date.
                </div>
              ) : (
                <div className="mt-2 space-y-2">
                  {selectedHomework.map((item, index) => {
                    const eventType = getEventType(item);

                    const canShowEarnedResults =
                      eventType === "completed" ||
                      eventType === "attempted";

                    const canShowPotentialRewards =
                      eventType === "due" ||
                      eventType === "incomplete";

                    const hasSuds =
                      typeof item.suds1 === "number" &&
                      Number.isFinite(item.suds1) &&
                      typeof item.suds2 === "number" &&
                      Number.isFinite(item.suds2);

                    const hasPoints =
                      typeof item.points === "number" &&
                      Number.isFinite(item.points) &&
                      item.points > 0;

                    const hasCoins =
                      typeof item.coins === "number" &&
                      Number.isFinite(item.coins) &&
                      item.coins > 0;

                    const resultItems: string[] = [];

                    // Completed or Attempted: rewards were earned.
                    if (canShowEarnedResults && hasSuds) {
                      resultItems.push(
                        `SUDS ${item.suds1} → ${item.suds2}`
                      );
                    }

                    if (canShowEarnedResults && hasPoints) {
                      resultItems.push(
                        `+${item.points} ${item.points === 1 ? "point" : "points"
                        }`
                      );
                    }

                    if (canShowEarnedResults && hasCoins) {
                      resultItems.push(
                        `+${item.coins} ${item.coins === 1 ? "coin" : "coins"
                        }`
                      );
                    }

                    // Due or Incomplete: rewards are still available.
                    if (canShowPotentialRewards) {
                      let hasRewardItem = false;

                      if (hasPoints) {
                        resultItems.push(
                          `Earn ${item.points} ${item.points === 1 ? "point" : "points"
                          }`
                        );

                        hasRewardItem = true;
                      }

                      if (hasCoins) {
                        resultItems.push(
                          `${hasRewardItem ? "" : "Earn "}${item.coins} ${item.coins === 1 ? "coin" : "coins"
                          }`
                        );
                      }
                    }

                    return (
                      <article
                        key={
                          item.recordId ??
                          `${selectedDateKey}-${item.activityName}-${index}`
                        }
                        className="rounded-xl border border-gray-200 bg-white p-3.5"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h4 className="break-words text-sm font-semibold text-gray-900">
                              {item.activityName ?? "Homework activity"}
                            </h4>
                            {item.activityType && (
                              <p className="mt-0.5 text-xs text-gray-500">
                                {item.activityType}
                              </p>
                            )}

                            {resultItems.length > 0 && (
                              <p className="mt-1 text-xs text-gray-600">
                                {resultItems.map((result, resultIndex) => (
                                  <React.Fragment key={result}>
                                    {resultIndex > 0 && (
                                      <span
                                        aria-hidden="true"
                                        className="mx-1.5 text-gray-300"
                                      >
                                        •
                                      </span>
                                    )}

                                    <span>{result}</span>
                                  </React.Fragment>
                                ))}
                              </p>
                            )}
                          </div>

                          <span
                            className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium ${EVENT_BADGE_CLASSES[eventType]}`}
                          >
                            {EVENT_LABELS[eventType]}
                          </span>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* Unscheduled homework */}
          {unscheduledHomework.length > 0 && (
            <section className="mt-5 border-t border-gray-200 pt-5">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-gray-900">
                  Unscheduled Homework
                </h3>

                <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                  {unscheduledHomework.length}
                </span>
              </div>

              <p className="mt-1 text-xs text-gray-500">
                Incomplete activities without a due date
              </p>

              <div className="mt-3 space-y-2">
                {unscheduledHomework.map((item, index) => {
                  const potentialRewards: string[] = [];

                  if (
                    typeof item.points === "number" &&
                    Number.isFinite(item.points) &&
                    item.points > 0
                  ) {
                    potentialRewards.push(
                      `${item.points} ${item.points === 1 ? "point" : "points"
                      }`
                    );
                  }

                  if (
                    typeof item.coins === "number" &&
                    Number.isFinite(item.coins) &&
                    item.coins > 0
                  ) {
                    potentialRewards.push(
                      `${item.coins} ${item.coins === 1 ? "coin" : "coins"
                      }`
                    );
                  }

                  return (
                    <article
                      key={
                        item.recordId ??
                        `unscheduled-${item.activityName}-${index}`
                      }
                      className="rounded-xl border border-gray-200 bg-gray-50 p-3"
                    >
                      <h4 className="text-sm font-medium text-gray-900">
                        {item.activityName ?? "Homework activity"}
                      </h4>

                      {item.activityType && (
                        <p className="mt-0.5 text-xs text-gray-500">
                          {item.activityType}
                        </p>
                      )}

                      {potentialRewards.length > 0 && (
                        <p className="mt-1 text-xs text-gray-600">
                          Earn {potentialRewards.join(" • ")}
                        </p>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          {homework.length === 0 && (
            <div className="mt-5 rounded-xl bg-gray-50 px-4 py-6 text-center">
              <CalendarDays className="mx-auto h-8 w-8 text-gray-300" />

              <p className="mt-2 text-sm font-medium text-gray-700">
                No homework calendar entries yet
              </p>

              <p className="mt-1 text-xs text-gray-500">
                Assigned and completed homework will appear here.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
